import clsx from 'clsx';
import { useEffect, useMemo, useRef, type ReactElement } from 'react';

import * as styles from './Highlight.sty.js';
import { createRangedHighlights } from './utils/create-ranged-highlights.js';
import { getOrCreateSharedHighlight } from './utils/get-or-create-shared-highlight.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { devLog } from '../../core/utils/logging.js';

const COMPONENT_NAME = 'Highlight';

/**
 * The props for the Highlight component.
 * @public
 */
export interface HighlightProps
  extends WithChildren,
    DOMProps,
    AriaLabelingProps,
    DataTestId,
    MaskingProps,
    StylingProps,
    BehaviorTrackingProps {
  /**
   * Either a substring or an array of multiple different substrings that
   * should be highlighted in the projected content.
   * Every occurrence of the string(s) is highlighted accordingly.
   */
  term: string | string[];

  /**
   * Property that determines whether the highlighting search is case-sensitive.
   * If set to `true`, the component searches for case sensitive occurrences.
   * @defaultValue false
   */
  caseSensitive?: boolean;
}

/**
 * Use the `Highlight` component to highlight one or more substrings within a
 * text.
 * @public
 */
export const Highlight = (props: HighlightProps): ReactElement => {
  const {
    children,
    term,
    caseSensitive = false,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const ariaLabelingProps = useAriaLabelingProps(remainingProps);
  const forwardProps = useSafeForwardProps(remainingProps, ariaLabelingProps);

  const rootRef = useRef<HTMLSpanElement>(null);

  const [behavioralTrackingProps] = useBehavioralTrackingProps(remainingProps);

  const terms = useMemo(() => {
    if (Array.isArray(term)) {
      return term.filter((t) => t);
    }

    return term ? [term] : [];
  }, [term]);

  const highlight = getOrCreateSharedHighlight();

  useEffect(() => {
    const currentElement = rootRef.current;
    const storedRanges: Array<Range> = [];

    if (currentElement) {
      const ranges = createRangedHighlights(
        currentElement,
        terms,
        caseSensitive,
        highlight,
      );
      storedRanges.push(...ranges);
    }

    return () => {
      for (const storedRange of storedRanges) {
        try {
          highlight.delete(storedRange);
        } catch {
          devLog('Failed to delete range', storedRange);
        }
      }
    };
  }, [children, highlight, terms, caseSensitive]);

  return (
    <span
      ref={rootRef}
      className={clsx(styles.wrapper, consumerClassName)}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      {children}
    </span>
  );
};

(Highlight as typeof Highlight & { displayName: string }).displayName =
  COMPONENT_NAME;
